import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children, userId }) => {
  const [ws, setWs] = useState(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [activeJobs, setActiveJobs] = useState(new Map());
  const connectingRef = React.useRef(false);

  const connect = useCallback(() => {
    if (!userId || connectingRef.current) return;

    // Prevent multiple simultaneous connections
    connectingRef.current = true;

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const wsUrl = apiUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    const websocket = new WebSocket(`${wsUrl}/ws/${userId}`);

    websocket.onopen = () => {
      console.log('🔌 WebSocket connected');
      setConnected(true);
      connectingRef.current = false;

      // Send ping every 30 seconds to keep alive
      const pingInterval = setInterval(() => {
        if (websocket.readyState === WebSocket.OPEN) {
          websocket.send('ping');
        }
      }, 30000);

      websocket.pingInterval = pingInterval;
    };

    websocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📬 Received:', data);

        if (data.type === 'job_update') {
          handleJobUpdate(data);
        } else if (data.type === 'pong') {
          // Ignore pong responses
        }
      } catch (error) {
        console.error('Error parsing message:', error);
      }
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    websocket.onclose = () => {
      console.log('🔌 WebSocket disconnected');
      setConnected(false);
      connectingRef.current = false;
      
      // Clear ping interval
      if (websocket.pingInterval) {
        clearInterval(websocket.pingInterval);
      }

      // Reconnect after 3 seconds
      setTimeout(() => {
        if (userId && !connectingRef.current) {
          connect();
        }
      }, 3000);
    };

    setWs(websocket);
  }, [userId]);

  const handleJobUpdate = (data) => {
    const { job_id, job_type, status, progress, result_data, error_message } = data;

    // Update active jobs
    setActiveJobs(prev => {
      const newJobs = new Map(prev);
      newJobs.set(job_id, {
        id: job_id,
        type: job_type,
        status,
        progress,
        result_data,
        error_message,
        timestamp: Date.now()
      });

      // Remove completed/failed jobs after 10 seconds
      if (status === 'completed' || status === 'failed') {
        setTimeout(() => {
          setActiveJobs(current => {
            const updated = new Map(current);
            updated.delete(job_id);
            return updated;
          });
        }, 10000);
      }

      return newJobs;
    });

    // Add notification
    const notificationMessage = getNotificationMessage(job_type, status, result_data);
    addNotification({
      id: `${job_id}-${Date.now()}`,
      type: status === 'completed' ? 'success' : status === 'failed' ? 'error' : 'info',
      message: notificationMessage,
      jobId: job_id,
      jobType: job_type,
      timestamp: Date.now()
    });
  };

  const getNotificationMessage = (jobType, status, resultData) => {
    const typeNames = {
      batch_extraction: 'Batch Extraction',
      try_on: 'Virtual Try-On',
      combine_outfit: 'Outfit Creation'
    };

    const typeName = typeNames[jobType] || jobType;

    if (status === 'completed') {
      if (jobType === 'batch_extraction' && resultData?.items_count) {
        return `✅ ${typeName} Complete! Added ${resultData.items_count} items to wardrobe.`;
      }
      return `✅ ${typeName} Complete!`;
    } else if (status === 'failed') {
      return `❌ ${typeName} Failed`;
    } else if (status === 'processing') {
      return `⏳ ${typeName} in progress...`;
    }
    return `${typeName}: ${status}`;
  };

  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev].slice(0, 5)); // Keep last 5

    // Auto-remove after 5 seconds
    setTimeout(() => {
      setNotifications(current => current.filter(n => n.id !== notification.id));
    }, 5000);
  };

  const clearNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  useEffect(() => {
    // Close existing connection before creating new one
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.close();
    }

    if (userId) {
      connect();
    }

    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [userId]);

  return (
    <WebSocketContext.Provider
      value={{
        connected,
        notifications,
        activeJobs,
        clearNotification,
        ws
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
